import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../supabase/config';
import { Button } from '../../components/admin/shared/Button';
import { useToast, ToastContainer } from '../../components/admin/shared/Toast';
import { useProjects, useSkills, useExperience, useTestimonials } from '../../lib/hooks';

const defaultSections = [
  { name: 'hero', title: 'Hero Section', visible: true, alwaysVisible: true },
  { name: 'about', title: 'About / Philosophy', visible: true },
  { name: 'skills', title: 'Tech Arsenal', visible: true },
  { name: 'projects', title: 'Featured Work', visible: true },
  { name: 'experience', title: 'Experience', visible: false },
  { name: 'testimonials', title: 'Testimonials', visible: false },
  { name: 'process', title: 'How I Work', visible: true, alwaysVisible: true },
  { name: 'cta', title: 'Contact / CTA', visible: true, alwaysVisible: true },
];

const Sections = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const { projects } = useProjects();
  const { skillsByCategory } = useSkills();
  const { experience } = useExperience();
  const { testimonials } = useTestimonials();
  const { toasts, addToast } = useToast();

  const fetchSections = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('sections')
        .select('*')
        .order('order');

      if (error && error.code !== '42P01') { // 42P01 = table doesn't exist
        throw error;
      }

      if (data && data.length > 0) {
        setSections(data);
      } else {
        // Use defaults
        setSections(defaultSections.map((s, i) => ({ ...s, id: `default-${i}`, order: i })));
      }
    } catch (error) {
      console.error('Error fetching sections:', error);
      // Use defaults on error
      setSections(defaultSections.map((s, i) => ({ ...s, id: `default-${i}`, order: i })));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSections();
  }, [fetchSections]);

  const getDataCount = (sectionName) => {
    switch (sectionName) {
      case 'skills':
        return Object.values(skillsByCategory).flat().length;
      case 'projects':
        return projects.length;
      case 'experience':
        return experience.length;
      case 'testimonials':
        return testimonials.length;
      default:
        return null;
    }
  };

  const toggleVisibility = async (section) => {
    if (section.alwaysVisible) return;

    const newVisible = !section.visible;

    try {
      // Try to update in database
      if (section.id && !section.id.startsWith('default-')) {
        const { error } = await supabase
          .from('sections')
          .update({ visible: newVisible })
          .eq('id', section.id);

        if (error) throw error;
      } else {
        // Create new record if it doesn't exist
        const { error } = await supabase
          .from('sections')
          .insert({
            name: section.name,
            title: section.title,
            visible: newVisible,
            order: section.order || 0
          });

        if (error && error.code !== '23505') { // Ignore duplicate key errors
          throw error;
        }
      }

      setSections(sections.map(s =>
        s.name === section.name ? { ...s, visible: newVisible } : s
      ));
      addToast(`Section ${newVisible ? 'enabled' : 'disabled'}`, 'success');
    } catch (error) {
      console.error('Error updating section:', error);
      // Still update locally even if DB fails
      setSections(sections.map(s =>
        s.name === section.name ? { ...s, visible: newVisible } : s
      ));
      addToast('Section visibility updated (local only)', 'info');
    }
  };

  if (loading) {
    return <div className="text-white">Loading...</div>;
  }

  return (
    <div>
      <ToastContainer toasts={toasts} />
      
      <h1 className="text-3xl font-bold text-white mb-8">Section Visibility</h1>

      <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
        <div className="space-y-4">
          {sections.map((section) => {
            const dataCount = getDataCount(section.name);
            return (
              <div
                key={section.name}
                className="flex items-center justify-between p-4 bg-slate-700 rounded-lg"
              >
                <div className="flex items-center gap-4">
                  <input
                    type="checkbox"
                    checked={section.visible}
                    onChange={() => toggleVisibility(section)}
                    disabled={section.alwaysVisible}
                    className="w-5 h-5 rounded"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-white font-medium">{section.title}</span>
                      {section.alwaysVisible && (
                        <span className="text-xs text-slate-400">(Always visible)</span>
                      )}
                    </div>
                    {dataCount !== null && (
                      <span className="text-sm text-slate-400">
                        ({dataCount} {dataCount === 1 ? 'item' : 'items'})
                      </span>
                    )}
                  </div>
                </div>
                {dataCount === 0 && !section.alwaysVisible && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      // Navigate to appropriate page
                      const routes = {
                        experience: '/admin/experience',
                        testimonials: '/admin/testimonials',
                        projects: '/admin/projects',
                        skills: '/admin/skills'
                      };
                      if (routes[section.name]) {
                        window.location.href = routes[section.name];
                      }
                    }}
                  >
                    + Add
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Sections;

